import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useWallet } from '../contexts/WalletContext';
import { db } from '../firebase/config';
import { 
    collection, 
    addDoc, 
    updateDoc, 
    deleteDoc, 
    doc,
    onSnapshot,
    query,
    orderBy,
    where,
    serverTimestamp,
    getDocs,
    writeBatch,
    increment
} from 'firebase/firestore';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Swal from 'sweetalert2';
import './Finance.css';

const Finance = () => {
    const { currentUser, userData, isAdmin } = useAuth();
    const { showToast } = useToast();
    const { walletAccounts, getWalletSummary, adminAdjustBalance } = useWallet();
    
    // State for transactions
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showTransactionModal, setShowTransactionModal] = useState(false);
    const [editingTransaction, setEditingTransaction] = useState(null);
    
    // State for income sources
    const [incomeSources, setIncomeSources] = useState([]);
    const [showSourceModal, setShowSourceModal] = useState(false);
    const [editingSource, setEditingSource] = useState(null);
    const [selectedSource, setSelectedSource] = useState(null);
    const [sourceTransactions, setSourceTransactions] = useState([]);
    const [showSourceDetails, setShowSourceDetails] = useState(false);
    
    // State for expenses
    const [expenses, setExpenses] = useState([]);
    const [showExpenseModal, setShowExpenseModal] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);
    
    // Wallet summary state
    const [walletSummary, setWalletSummary] = useState(null);
    
    // Filters
    const [filters, setFilters] = useState({
        department: '',
        type: '',
        month: '',
        sourceId: ''
    });
    
    // Form states
    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        description: '',
        department: '',
        reason: '',
        type: 'income',
        amount: '',
        notes: '',
        sourceId: '',
        sourceName: '',
        category: ''
    });

    const [sourceFormData, setSourceFormData] = useState({
        name: '',
        description: '',
        category: '',
        targetAmount: '',
        startDate: new Date().toISOString().split('T')[0],
        icon: 'fa-folder'
    });

    const [expenseFormData, setExpenseFormData] = useState({
        name: '',
        description: '',
        category: '',
        budgetAmount: '',
        startDate: new Date().toISOString().split('T')[0]
    });

    // Department options with wallet mapping
    const departments = [
        { value: 'missions', label: 'Missions Support', walletAccount: 'missions' },
        { value: 'social', label: 'Social Lunch', walletAccount: 'social_lunch' },
        { value: 'sem', label: 'SEM Subscription', walletAccount: 'main' },
        { value: 'donations', label: 'Donations', walletAccount: 'contributions' },
        { value: 'development', label: 'Development', walletAccount: 'development' },
        { value: 'projects', label: 'Projects', walletAccount: 'projects' },
        { value: 'sabbath_fare', label: 'Sabbath Outing Fare', walletAccount: 'sabbath_fare' },
        { value: 'other', label: 'Other', walletAccount: 'main' }
    ];

    // Category options
    const categories = [
        'Tithe',
        'Offering',
        'Donation',
        'Project Funding',
        'Event Income',
        'Subscription',
        'Other'
    ];

    // Reason options based on department
    const getReasonOptions = (department) => {
        const reasons = {
            missions: ['Outreach Program', 'Missionary Support', 'Evangelism Materials', 'Transport'],
            social: ['Monthly Lunch', 'Special Event', 'Supplies', 'Venue'],
            sem: ['Monthly Subscription', 'Annual Subscription', 'Materials'],
            donations: ['Tithe', 'Offering', 'Special Donation', 'Memorial'],
            development: ['Building Fund', 'Maintenance', 'Equipment', 'Renovation'],
            projects: ['Project Funding', 'Materials', 'Labor', 'Equipment'],
            sabbath_fare: ['Transportation', 'Bus Hire', 'Fuel'],
            other: ['General', 'Emergency', 'Miscellaneous']
        };
        return reasons[department] || ['General'];
    };

    // Load all data
    useEffect(() => {
        loadTransactions();
        loadIncomeSources();
        loadExpenses();
        if (isAdmin) {
            loadWalletSummary();
        }
    }, []);

    // Load wallet summary for admin
    const loadWalletSummary = async () => {
        const summary = await getWalletSummary();
        if (summary) {
            setWalletSummary(summary);
        }
    };

    // Load transactions
    const loadTransactions = () => {
        const transactionsQuery = query(
            collection(db, 'transactions'),
            orderBy('date', 'desc')
        );

        const unsubscribe = onSnapshot(transactionsQuery, (snapshot) => {
            const transactionsData = [];
            snapshot.forEach(doc => {
                transactionsData.push({ id: doc.id, ...doc.data() });
            });
            setTransactions(transactionsData);
            setLoading(false);
        }, (error) => {
            console.error('Error loading transactions:', error);
            showToast('Error loading transactions', 'error');
            setLoading(false);
        });

        return unsubscribe;
    };

    // Load income sources
    const loadIncomeSources = () => {
        const sourcesQuery = query(
            collection(db, 'incomeSources'),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(sourcesQuery, (snapshot) => {
            const sourcesData = [];
            snapshot.forEach(doc => {
                sourcesData.push({ id: doc.id, ...doc.data() });
            });
            setIncomeSources(sourcesData);
        }, (error) => {
            console.error('Error loading income sources:', error);
        });

        return unsubscribe;
    };

    // Load expenses
    const loadExpenses = () => {
        const expensesQuery = query(
            collection(db, 'expenses'),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(expensesQuery, (snapshot) => {
            const expensesData = [];
            snapshot.forEach(doc => {
                expensesData.push({ id: doc.id, ...doc.data() });
            });
            setExpenses(expensesData);
        }, (error) => {
            console.error('Error loading expenses:', error);
        });

        return unsubscribe;
    };

    // Load source transactions
    const loadSourceTransactions = async (sourceId) => {
        try {
            const transactionsQuery = query(
                collection(db, 'transactions'),
                where('sourceId', '==', sourceId),
                orderBy('date', 'desc')
            );
            const snapshot = await getDocs(transactionsQuery);
            const data = [];
            snapshot.forEach(doc => {
                data.push({ id: doc.id, ...doc.data() });
            });
            setSourceTransactions(data);
        } catch (error) {
            console.error('Error loading source transactions:', error);
            showToast('Error loading source transactions', 'error');
        }
    };

    // Get admin name
    const getAdminName = async () => {
        try {
            const adminDoc = await getDocs(query(
                collection(db, 'admins'),
                where('email', '==', currentUser.email)
            ));
            if (!adminDoc.empty) {
                const adminData = adminDoc.docs[0].data();
                return adminData.fullName || adminData.name || currentUser.email;
            }
            return userData?.fullName || currentUser.email;
        } catch (error) {
            console.error('Error getting admin name:', error);
            return userData?.fullName || currentUser.email;
        }
    };

    // Format time in 12hr format
    const formatTime12hr = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (e) {
            return dateString;
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        if (amount === undefined || amount === null) return 'Ksh 0';
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 2
        }).format(amount);
    };

    // Handle input changes
    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));

        if (id === 'department') {
            setFormData(prev => ({ ...prev, reason: '' }));
        }
    };

    const handleSourceInputChange = (e) => {
        const { id, value } = e.target;
        setSourceFormData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handleExpenseInputChange = (e) => {
        const { id, value } = e.target;
        setExpenseFormData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handleFilterChange = (e) => {
        const { id, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [id]: value
        }));
    };

    // Reset forms
    const resetForm = () => {
        setFormData({
            date: new Date().toISOString().split('T')[0],
            description: '',
            department: '',
            reason: '',
            type: 'income',
            amount: '',
            notes: '',
            sourceId: '',
            sourceName: '',
            category: ''
        });
        setEditingTransaction(null);
    };

    const resetSourceForm = () => {
        setSourceFormData({
            name: '',
            description: '',
            category: '',
            targetAmount: '',
            startDate: new Date().toISOString().split('T')[0],
            icon: 'fa-folder'
        });
        setEditingSource(null);
    };

    const resetExpenseForm = () => {
        setExpenseFormData({
            name: '',
            description: '',
            category: '',
            budgetAmount: '',
            startDate: new Date().toISOString().split('T')[0]
        });
        setEditingExpense(null);
    };

    // Validate forms
    const validateForm = () => {
        if (!formData.description.trim()) {
            showToast('Please enter a description', 'error');
            return false;
        }
        if (!formData.department) {
            showToast('Please select a department', 'error');
            return false;
        }
        if (!formData.reason) {
            showToast('Please select a reason', 'error');
            return false;
        }
        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            showToast('Please enter a valid amount', 'error');
            return false;
        }
        if (formData.type === 'income' && !formData.sourceId && !formData.sourceName) {
            showToast('Please select or enter an income source', 'error');
            return false;
        }
        return true;
    };

    const validateSourceForm = () => {
        if (!sourceFormData.name.trim()) {
            showToast('Please enter a source name', 'error');
            return false;
        }
        if (!sourceFormData.category) {
            showToast('Please select a category', 'error');
            return false;
        }
        return true;
    };

    const validateExpenseForm = () => {
        if (!expenseFormData.name.trim()) {
            showToast('Please enter an expense name', 'error');
            return false;
        }
        if (!expenseFormData.category) {
            showToast('Please select a category', 'error');
            return false;
        }
        if (expenseFormData.budgetAmount && parseFloat(expenseFormData.budgetAmount) <= 0) {
            showToast('Please enter a valid budget amount', 'error');
            return false;
        }
        return true;
    };

    // Handle income source submission
    const handleSourceSubmit = async (e) => {
        e.preventDefault();

        if (!isAdmin) {
            showToast('Only administrators can manage income sources', 'error');
            return;
        }

        if (!validateSourceForm()) {
            return;
        }

        setLoading(true);

        try {
            const adminName = await getAdminName();
            const sourceData = {
                name: sourceFormData.name.trim(),
                description: sourceFormData.description.trim(),
                category: sourceFormData.category,
                targetAmount: parseFloat(sourceFormData.targetAmount) || 0,
                startDate: sourceFormData.startDate,
                icon: sourceFormData.icon,
                totalIncome: 0,
                transactionCount: 0,
                createdBy: currentUser.uid,
                createdByName: adminName,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                updatedBy: currentUser.uid,
                updatedByName: adminName,
                status: 'active'
            };

            if (editingSource) {
                const sourceRef = doc(db, 'incomeSources', editingSource.id);
                await updateDoc(sourceRef, {
                    ...sourceData,
                    updatedAt: serverTimestamp(),
                    updatedBy: currentUser.uid,
                    updatedByName: adminName
                });
                showToast('Income source updated successfully!', 'success');
            } else {
                await addDoc(collection(db, 'incomeSources'), sourceData);
                showToast('Income source added successfully!', 'success');
            }

            setShowSourceModal(false);
            resetSourceForm();
        } catch (error) {
            console.error('Error saving income source:', error);
            showToast('Error saving income source', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle expense submission
    const handleExpenseSubmit = async (e) => {
        e.preventDefault();

        if (!isAdmin) {
            showToast('Only administrators can manage expenses', 'error');
            return;
        }

        if (!validateExpenseForm()) {
            return;
        }

        setLoading(true);

        try {
            const adminName = await getAdminName();
            const expenseData = {
                name: expenseFormData.name.trim(),
                description: expenseFormData.description.trim(),
                category: expenseFormData.category,
                budgetAmount: parseFloat(expenseFormData.budgetAmount) || 0,
                startDate: expenseFormData.startDate,
                totalExpense: 0,
                transactionCount: 0,
                createdBy: currentUser.uid,
                createdByName: adminName,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                updatedBy: currentUser.uid,
                updatedByName: adminName,
                status: 'active'
            };

            if (editingExpense) {
                const expenseRef = doc(db, 'expenses', editingExpense.id);
                await updateDoc(expenseRef, {
                    ...expenseData,
                    updatedAt: serverTimestamp(),
                    updatedBy: currentUser.uid,
                    updatedByName: adminName
                });
                showToast('Expense updated successfully!', 'success');
            } else {
                await addDoc(collection(db, 'expenses'), expenseData);
                showToast('Expense added successfully!', 'success');
            }

            setShowExpenseModal(false);
            resetExpenseForm();
        } catch (error) {
            console.error('Error saving expense:', error);
            showToast('Error saving expense', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle transaction submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isAdmin) {
            showToast('Only administrators can manage transactions', 'error');
            return;
        }

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const adminName = await getAdminName();
            const amount = parseFloat(formData.amount);
            const department = departments.find(d => d.value === formData.department);
            
            const transactionData = {
                date: formData.date,
                description: formData.description.trim(),
                department: formData.department,
                reason: formData.reason,
                type: formData.type,
                amount: amount,
                notes: formData.notes.trim(),
                recordedBy: currentUser.uid,
                recordedByName: adminName,
                recordedAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                updatedBy: currentUser.uid,
                updatedByName: adminName
            };

            // Add source info for income
            if (formData.type === 'income') {
                transactionData.sourceId = formData.sourceId;
                transactionData.sourceName = formData.sourceName || 
                    (formData.sourceId ? incomeSources.find(s => s.id === formData.sourceId)?.name : '');
                transactionData.category = formData.category;
            }

            if (editingTransaction) {
                const transactionRef = doc(db, 'transactions', editingTransaction.id);
                await updateDoc(transactionRef, transactionData);
                showToast('Transaction updated successfully!', 'success');
            } else {
                await addDoc(collection(db, 'transactions'), transactionData);

                // Update source total if income
                if (formData.type === 'income' && formData.sourceId) {
                    const sourceRef = doc(db, 'incomeSources', formData.sourceId);
                    await updateDoc(sourceRef, {
                        totalIncome: increment(amount),
                        transactionCount: increment(1),
                        updatedAt: serverTimestamp(),
                        updatedBy: currentUser.uid,
                        updatedByName: adminName
                    });
                }
                
                // For expenses, update wallet if it's a wallet-funded expense
                if (formData.type === 'expense' && department?.walletAccount) {
                    // This would be integrated when users pay via wallet
                    console.log(`Expense recorded for ${department.label} - wallet account: ${department.walletAccount}`);
                }
            }

            setShowTransactionModal(false);
            resetForm();
            if (isAdmin) {
                loadWalletSummary();
            }
        } catch (error) {
            console.error('Error saving transaction:', error);
            showToast('Error saving transaction', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle transaction edit
    const handleEdit = (transaction) => {
        setEditingTransaction(transaction);
        setFormData({
            date: transaction.date || new Date().toISOString().split('T')[0],
            description: transaction.description || '',
            department: transaction.department || '',
            reason: transaction.reason || '',
            type: transaction.type || 'income',
            amount: transaction.amount?.toString() || '',
            notes: transaction.notes || '',
            sourceId: transaction.sourceId || '',
            sourceName: transaction.sourceName || '',
            category: transaction.category || ''
        });
        setShowTransactionModal(true);
    };

    // Handle transaction delete
    const handleDelete = async (transactionId) => {
        if (!isAdmin) {
            showToast('Only administrators can delete transactions', 'error');
            return;
        }

        const result = await Swal.fire({
            title: 'Delete Transaction?',
            text: 'This action cannot be undone!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(db, 'transactions', transactionId));
                showToast('Transaction deleted successfully!', 'success');
            } catch (error) {
                console.error('Error deleting transaction:', error);
                showToast('Error deleting transaction', 'error');
            }
        }
    };

    // Handle source card click
    const handleSourceClick = async (source) => {
        setSelectedSource(source);
        await loadSourceTransactions(source.id);
        setShowSourceDetails(true);
    };

    // Handle source edit
    const handleEditSource = (source) => {
        setEditingSource(source);
        setSourceFormData({
            name: source.name || '',
            description: source.description || '',
            category: source.category || '',
            targetAmount: source.targetAmount?.toString() || '',
            startDate: source.startDate || new Date().toISOString().split('T')[0],
            icon: source.icon || 'fa-folder'
        });
        setShowSourceModal(true);
    };

    // Handle source delete
    const handleDeleteSource = async (sourceId) => {
        const result = await Swal.fire({
            title: 'Delete Income Source?',
            text: 'This will also delete all transactions under this source. This action cannot be undone!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const transactionsQuery = query(
                    collection(db, 'transactions'),
                    where('sourceId', '==', sourceId)
                );
                const snapshot = await getDocs(transactionsQuery);
                const batch = writeBatch(db);
                snapshot.docs.forEach(doc => {
                    batch.delete(doc.ref);
                });
                batch.delete(doc(db, 'incomeSources', sourceId));
                await batch.commit();
                showToast('Income source and related transactions deleted successfully!', 'success');
            } catch (error) {
                console.error('Error deleting source:', error);
                showToast('Error deleting source', 'error');
            }
        }
    };

    // Export to PDF
    const exportToPDF = async (data, title, source = null) => {
        const pdf = new jsPDF();
        
        try {
            // Add logo
            const logoUrl = '/Logo1.png';
            const logo = await fetch(logoUrl)
                .then(res => res.blob())
                .then(blob => {
                    return new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result);
                        reader.readAsDataURL(blob);
                    });
                })
                .catch(() => null);

            if (logo) {
                pdf.addImage(logo, 'PNG', 14, 10, 30, 30);
            }

            // Header
            pdf.setFontSize(20);
            pdf.setTextColor(16, 52, 166);
            pdf.text('WHITE DOVES MINISTRY', 50, 20);
            
            pdf.setFontSize(16);
            pdf.setTextColor(0, 0, 0);
            pdf.text(title, 50, 30);

            // Source info if provided
            if (source) {
                pdf.setFontSize(12);
                pdf.text(`Source: ${source.name}`, 14, 50);
                pdf.text(`Category: ${source.category}`, 14, 58);
                pdf.text(`Target: ${formatCurrency(source.targetAmount || 0)}`, 14, 66);
                pdf.text(`Total Income: ${formatCurrency(source.totalIncome || 0)}`, 14, 74);
                pdf.text(`Progress: ${source.targetAmount ? Math.round((source.totalIncome / source.targetAmount) * 100) : 0}%`, 14, 82);
            }

            // Date and admin info
            const now = new Date();
            pdf.setFontSize(10);
            pdf.setTextColor(100);
            pdf.text(`Generated on: ${now.toLocaleDateString()} at ${formatTime12hr(now)}`, 14, source ? 92 : 50);
            pdf.text(`Generated by: ${userData?.fullName || currentUser.email}`, 14, source ? 98 : 56);

            // Table
            pdf.autoTable({
                startY: source ? 105 : 65,
                head: [['Date', 'Description', 'Department', 'Reason', 'Type', 'Amount (Ksh)']],
                body: data.map(item => [
                    formatDate(item.date),
                    item.description,
                    departments.find(d => d.value === item.department)?.label || item.department,
                    item.reason,
                    item.type,
                    item.amount.toFixed(2)
                ]),
                foot: [[
                    '', '', '', '', 'Total',
                    data.reduce((sum, item) => sum + item.amount, 0).toFixed(2)
                ]],
                theme: 'grid',
                headStyles: {
                    fillColor: [16, 52, 166],
                    textColor: 255,
                    fontStyle: 'bold'
                },
                footStyles: {
                    fillColor: [240, 240, 240],
                    textColor: [0, 0, 0],
                    fontStyle: 'bold'
                },
                alternateRowStyles: {
                    fillColor: [245, 245, 245]
                }
            });

            pdf.save(`${title.replace(/\s+/g, '_')}_${now.toISOString().split('T')[0]}.pdf`);
            showToast('PDF exported successfully!', 'success');
        } catch (error) {
            console.error('Error exporting PDF:', error);
            showToast('Error exporting PDF', 'error');
        }
    };

    // Filter transactions
    const filteredTransactions = transactions.filter(transaction => {
        if (filters.department && transaction.department !== filters.department) return false;
        if (filters.type && transaction.type !== filters.type) return false;
        if (filters.sourceId && transaction.sourceId !== filters.sourceId) return false;
        if (filters.month) {
            const transactionDate = new Date(transaction.date);
            const [year, month] = filters.month.split('-');
            if (transactionDate.getFullYear() !== parseInt(year) || 
                transactionDate.getMonth() + 1 !== parseInt(month)) {
                return false;
            }
        }
        return true;
    });

    // Calculate statistics
    const stats = filteredTransactions.reduce((acc, transaction) => {
        const amount = parseFloat(transaction.amount) || 0;
        if (transaction.type === 'income') {
            acc.totalIncome += amount;
        } else {
            acc.totalExpenses += amount;
        }
        acc.balance = acc.totalIncome - acc.totalExpenses;

        if (!acc.departments[transaction.department]) {
            acc.departments[transaction.department] = {
                income: 0,
                expense: 0,
                balance: 0
            };
        }
        if (transaction.type === 'income') {
            acc.departments[transaction.department].income += amount;
        } else {
            acc.departments[transaction.department].expense += amount;
        }
        acc.departments[transaction.department].balance = 
            acc.departments[transaction.department].income - acc.departments[transaction.department].expense;

        return acc;
    }, {
        totalIncome: 0,
        totalExpenses: 0,
        balance: 0,
        departments: {}
    });

    if (loading && transactions.length === 0) {
        return (
            <div className="finance-loading">
                <i className="fas fa-spinner fa-spin"></i>
                <p>Loading finances...</p>
            </div>
        );
    }

    return (
        <div className="finance-page">
            <header className="finance-header">
                <div className="finance-logo">WHITE DOVES MINISTRY</div>
                <div className="finance-subtitle">Financial Management</div>
            </header>

            <div className="finance-main-content">
                <h1 className="finance-page-title">Financial Management</h1>

                {/* Admin Actions */}
                {isAdmin && (
                    <div className="finance-admin-actions">
                        <button
                            className="finance-admin-btn"
                            onClick={() => {
                                resetForm();
                                setShowTransactionModal(true);
                            }}
                        >
                            <i className="fas fa-plus-circle"></i> Add Transaction
                        </button>
                        <button
                            className="finance-admin-btn"
                            onClick={() => {
                                resetSourceForm();
                                setShowSourceModal(true);
                            }}
                        >
                            <i className="fas fa-folder-plus"></i> Add Income Source
                        </button>
                        <button
                            className="finance-admin-btn"
                            onClick={() => {
                                resetExpenseForm();
                                setShowExpenseModal(true);
                            }}
                        >
                            <i className="fas fa-chart-line"></i> Add Expense Category
                        </button>
                    </div>
                )}

                {/* Wallet Summary for Admin */}
                {isAdmin && walletSummary && (
                    <div className="finance-wallet-summary">
                        <h3>Wallet Accounts Summary</h3>
                        <div className="wallet-summary-grid">
                            {walletAccounts.map(account => {
                                const total = walletSummary.accountTotals?.[account.id] || 0;
                                return (
                                    <div key={account.id} className="wallet-summary-card">
                                        <div className="wallet-summary-name">{account.name}</div>
                                        <div className="wallet-summary-amount">{formatCurrency(total)}</div>
                                    </div>
                                );
                            })}
                            <div className="wallet-summary-card total">
                                <div className="wallet-summary-name">Total Balance</div>
                                <div className="wallet-summary-amount">{formatCurrency(walletSummary.totalBalance || 0)}</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="finance-filters">
                    <select
                        id="department"
                        className="finance-filter-select"
                        value={filters.department}
                        onChange={handleFilterChange}
                    >
                        <option value="">All Departments</option>
                        {departments.map(dept => (
                            <option key={dept.value} value={dept.value}>
                                {dept.label}
                            </option>
                        ))}
                    </select>

                    <select
                        id="type"
                        className="finance-filter-select"
                        value={filters.type}
                        onChange={handleFilterChange}
                    >
                        <option value="">All Types</option>
                        <option value="income">Income</option>
                        <option value="expense">Expense</option>
                    </select>

                    <select
                        id="sourceId"
                        className="finance-filter-select"
                        value={filters.sourceId}
                        onChange={handleFilterChange}
                    >
                        <option value="">All Sources</option>
                        {incomeSources.map(source => (
                            <option key={source.id} value={source.id}>
                                {source.name}
                            </option>
                        ))}
                    </select>

                    <input
                        type="month"
                        id="month"
                        className="finance-filter-input"
                        value={filters.month}
                        onChange={handleFilterChange}
                    />

                    <button
                        className="finance-export-btn"
                        onClick={() => exportToPDF(filteredTransactions, 'Financial_Report')}
                        disabled={filteredTransactions.length === 0}
                    >
                        <i className="fas fa-file-pdf"></i> Export to PDF
                    </button>
                </div>

                {/* Summary Cards */}
                <div className="finance-summary">
                    <div className="finance-summary-card">
                        <h3 className="finance-summary-title">Total Income</h3>
                        <div className="finance-summary-amount income">
                            {formatCurrency(stats.totalIncome)}
                        </div>
                    </div>
                    <div className="finance-summary-card">
                        <h3 className="finance-summary-title">Total Expenses</h3>
                        <div className="finance-summary-amount expense">
                            {formatCurrency(stats.totalExpenses)}
                        </div>
                    </div>
                    <div className="finance-summary-card">
                        <h3 className="finance-summary-title">Current Balance</h3>
                        <div className="finance-summary-amount balance">
                            {formatCurrency(stats.balance)}
                        </div>
                    </div>
                </div>

                {/* Income Sources Cards */}
                {isAdmin && incomeSources.length > 0 && (
                    <div className="finance-sources-section">
                        <h2 className="finance-section-title">Income Sources</h2>
                        <div className="finance-sources-grid">
                            {incomeSources.map(source => {
                                const progress = source.targetAmount 
                                    ? Math.min(Math.round((source.totalIncome / source.targetAmount) * 100), 100)
                                    : 0;

                                return (
                                    <div 
                                        key={source.id} 
                                        className="finance-source-card"
                                        onClick={() => handleSourceClick(source)}
                                    >
                                        <div className="finance-source-header">
                                            <i className={`fas ${source.icon || 'fa-folder'}`}></i>
                                            <h3 className="finance-source-name">{source.name}</h3>
                                            {source.category && (
                                                <span className="finance-source-category">{source.category}</span>
                                            )}
                                        </div>
                                        
                                        <p className="finance-source-description">{source.description}</p>
                                        
                                        <div className="finance-source-stats">
                                            <div className="finance-source-stat">
                                                <span>Total Income:</span>
                                                <strong className="income">{formatCurrency(source.totalIncome || 0)}</strong>
                                            </div>
                                            {source.targetAmount > 0 && (
                                                <>
                                                    <div className="finance-source-stat">
                                                        <span>Target:</span>
                                                        <strong>{formatCurrency(source.targetAmount)}</strong>
                                                    </div>
                                                    <div className="finance-source-progress">
                                                        <div className="finance-source-progress-bar">
                                                            <div 
                                                                className="finance-source-progress-fill"
                                                                style={{ width: `${progress}%` }}
                                                            ></div>
                                                        </div>
                                                        <span className="finance-source-progress-text">{progress}%</span>
                                                    </div>
                                                </>
                                            )}
                                            <div className="finance-source-stat">
                                                <span>Transactions:</span>
                                                <strong>{source.transactionCount || 0}</strong>
                                            </div>
                                        </div>

                                        <div className="finance-source-footer">
                                            <small>
                                                Updated: {source.updatedAt ? formatTime12hr(source.updatedAt) : 'N/A'} by {source.updatedByName || 'Unknown'}
                                            </small>
                                            <div className="finance-source-actions">
                                                <button 
                                                    className="finance-source-edit"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEditSource(source);
                                                    }}
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                                <button 
                                                    className="finance-source-delete"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteSource(source.id);
                                                    }}
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                                <button 
                                                    className="finance-source-export"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        exportToPDF(sourceTransactions, `${source.name}_Report`, source);
                                                    }}
                                                >
                                                    <i className="fas fa-file-pdf"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Source Details Modal */}
                {showSourceDetails && selectedSource && (
                    <div className="finance-modal" onClick={() => setShowSourceDetails(false)}>
                        <div className="finance-modal-content finance-source-details" onClick={e => e.stopPropagation()}>
                            <div className="finance-modal-header">
                                <div>
                                    <h3 className="finance-modal-title">{selectedSource.name}</h3>
                                    <p className="finance-source-subtitle">{selectedSource.description}</p>
                                </div>
                                <button
                                    className="finance-modal-close"
                                    onClick={() => setShowSourceDetails(false)}
                                >
                                    &times;
                                </button>
                            </div>

                            <div className="finance-source-stats-detailed">
                                <div className="finance-stat-box">
                                    <span>Total Income</span>
                                    <strong className="income">{formatCurrency(selectedSource.totalIncome || 0)}</strong>
                                </div>
                                {selectedSource.targetAmount > 0 && (
                                    <div className="finance-stat-box">
                                        <span>Target</span>
                                        <strong>{formatCurrency(selectedSource.targetAmount)}</strong>
                                    </div>
                                )}
                                <div className="finance-stat-box">
                                    <span>Transactions</span>
                                    <strong>{sourceTransactions.length}</strong>
                                </div>
                            </div>

                            <div className="finance-source-transactions">
                                <h4>Transactions</h4>
                                <table className="finance-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Description</th>
                                            <th>Department</th>
                                            <th>Reason</th>
                                            <th>Amount</th>
                                            <th>Recorded By</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sourceTransactions.length === 0 ? (
                                            <tr>
                                                <td colSpan="6" className="finance-empty">
                                                    No transactions for this source
                                                </td>
                                            </tr>
                                        ) : (
                                            sourceTransactions.map(transaction => (
                                                <tr key={transaction.id}>
                                                    <td>{formatDate(transaction.date)}</td>
                                                    <td>{transaction.description}</td>
                                                    <td>{departments.find(d => d.value === transaction.department)?.label || transaction.department}</td>
                                                    <td>{transaction.reason}</td>
                                                    <td className="income">{formatCurrency(transaction.amount)}</td>
                                                    <td>{transaction.recordedByName}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="finance-modal-footer">
                                <button
                                    className="finance-btn-primary"
                                    onClick={() => exportToPDF(sourceTransactions, `${selectedSource.name}_Transactions`, selectedSource)}
                                    disabled={sourceTransactions.length === 0}
                                >
                                    <i className="fas fa-file-pdf"></i> Export to PDF
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Transactions Table */}
                <div className="finance-table-container">
                    <h2 className="finance-section-title">Recent Transactions</h2>
                    <table className="finance-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Description</th>
                                <th>Department</th>
                                <th>Reason</th>
                                <th>Type</th>
                                <th>Amount</th>
                                <th>Recorded By</th>
                                {isAdmin && <th>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTransactions.length === 0 ? (
                                <tr>
                                    <td colSpan={isAdmin ? 8 : 7} className="finance-empty">
                                        <i className="fas fa-money-bill-wave"></i>
                                        <h3>No Transactions Found</h3>
                                        <p>Add your first transaction to get started</p>
                                    </td>
                                </tr>
                            ) : (
                                filteredTransactions.slice(0, 10).map(transaction => (
                                    <tr
                                        key={transaction.id}
                                        className={transaction.type === 'income' ? 'income-row' : 'expense-row'}
                                    >
                                        <td>{formatDate(transaction.date)}</td>
                                        <td>{transaction.description}</td>
                                        <td>
                                            <span className="finance-department-tag">
                                                {departments.find(d => d.value === transaction.department)?.label || transaction.department}
                                            </span>
                                        </td>
                                        <td>{transaction.reason}</td>
                                        <td>
                                            <span className={`finance-type-badge ${transaction.type}`}>
                                                {transaction.type}
                                            </span>
                                        </td>
                                        <td className={transaction.type === 'income' ? 'income' : 'expense'}>
                                            {formatCurrency(transaction.amount)}
                                        </td>
                                        <td>
                                            <div className="finance-recorded-by">
                                                {transaction.recordedByName}
                                                <small>{transaction.recordedAt ? formatTime12hr(transaction.recordedAt) : ''}</small>
                                            </div>
                                        </td>
                                        {isAdmin && (
                                            <td>
                                                <button
                                                    className="finance-action-btn edit"
                                                    onClick={() => handleEdit(transaction)}
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                                <button
                                                    className="finance-action-btn delete"
                                                    onClick={() => handleDelete(transaction.id)}
                                                >
                                                    <i className="fas fa-trash-alt"></i>
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Income Source Modal */}
            {showSourceModal && (
                <div className="finance-modal" onClick={() => setShowSourceModal(false)}>
                    <div className="finance-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="finance-modal-header">
                            <h3 className="finance-modal-title">
                                {editingSource ? 'Edit Income Source' : 'Add New Income Source'}
                            </h3>
                            <button
                                className="finance-modal-close"
                                onClick={() => setShowSourceModal(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <form onSubmit={handleSourceSubmit} className="finance-form">
                            <div className="finance-form-group">
                                <label htmlFor="sourceName">Source Name *</label>
                                <input
                                    type="text"
                                    id="name"
                                    className="finance-form-control"
                                    placeholder="e.g., Building Fund, Tithe, Donations"
                                    value={sourceFormData.name}
                                    onChange={handleSourceInputChange}
                                    required
                                />
                            </div>

                            <div className="finance-form-group">
                                <label htmlFor="sourceDescription">Description</label>
                                <textarea
                                    id="description"
                                    className="finance-form-control"
                                    rows="3"
                                    placeholder="Describe this income source"
                                    value={sourceFormData.description}
                                    onChange={handleSourceInputChange}
                                ></textarea>
                            </div>

                            <div className="finance-form-row">
                                <div className="finance-form-group">
                                    <label htmlFor="sourceCategory">Category *</label>
                                    <select
                                        id="category"
                                        className="finance-form-control"
                                        value={sourceFormData.category}
                                        onChange={handleSourceInputChange}
                                        required
                                    >
                                        <option value="">Select Category</option>
                                        {categories.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="finance-form-group">
                                    <label htmlFor="sourceIcon">Icon</label>
                                    <select
                                        id="icon"
                                        className="finance-form-control"
                                        value={sourceFormData.icon}
                                        onChange={handleSourceInputChange}
                                    >
                                        <option value="fa-folder">Folder</option>
                                        <option value="fa-church">Church</option>
                                        <option value="fa-hand-holding-heart">Heart</option>
                                        <option value="fa-building">Building</option>
                                        <option value="fa-gift">Gift</option>
                                        <option value="fa-donate">Donation</option>
                                    </select>
                                </div>
                            </div>

                            <div className="finance-form-row">
                                <div className="finance-form-group">
                                    <label htmlFor="sourceTargetAmount">Target Amount (Optional)</label>
                                    <input
                                        type="number"
                                        id="targetAmount"
                                        className="finance-form-control"
                                        placeholder="0"
                                        value={sourceFormData.targetAmount}
                                        onChange={handleSourceInputChange}
                                        min="0"
                                        step="0.01"
                                    />
                                </div>

                                <div className="finance-form-group">
                                    <label htmlFor="sourceStartDate">Start Date</label>
                                    <input
                                        type="date"
                                        id="startDate"
                                        className="finance-form-control"
                                        value={sourceFormData.startDate}
                                        onChange={handleSourceInputChange}
                                    />
                                </div>
                            </div>

                            <div className="finance-form-actions">
                                <button
                                    type="button"
                                    className="finance-btn-secondary"
                                    onClick={() => setShowSourceModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="finance-btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-save"></i>
                                            {editingSource ? 'Update Source' : 'Add Source'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Expense Modal */}
            {showExpenseModal && (
                <div className="finance-modal" onClick={() => setShowExpenseModal(false)}>
                    <div className="finance-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="finance-modal-header">
                            <h3 className="finance-modal-title">
                                {editingExpense ? 'Edit Expense Category' : 'Add Expense Category'}
                            </h3>
                            <button
                                className="finance-modal-close"
                                onClick={() => setShowExpenseModal(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <form onSubmit={handleExpenseSubmit} className="finance-form">
                            <div className="finance-form-group">
                                <label htmlFor="expenseName">Expense Name *</label>
                                <input
                                    type="text"
                                    id="name"
                                    className="finance-form-control"
                                    placeholder="e.g., Utilities, Salaries, Supplies"
                                    value={expenseFormData.name}
                                    onChange={handleExpenseInputChange}
                                    required
                                />
                            </div>

                            <div className="finance-form-group">
                                <label htmlFor="expenseDescription">Description</label>
                                <textarea
                                    id="description"
                                    className="finance-form-control"
                                    rows="3"
                                    placeholder="Describe this expense category"
                                    value={expenseFormData.description}
                                    onChange={handleExpenseInputChange}
                                ></textarea>
                            </div>

                            <div className="finance-form-row">
                                <div className="finance-form-group">
                                    <label htmlFor="expenseCategory">Category *</label>
                                    <select
                                        id="category"
                                        className="finance-form-control"
                                        value={expenseFormData.category}
                                        onChange={handleExpenseInputChange}
                                        required
                                    >
                                        <option value="">Select Category</option>
                                        <option value="Operational">Operational</option>
                                        <option value="Staff">Staff</option>
                                        <option value="Events">Events</option>
                                        <option value="Maintenance">Maintenance</option>
                                        <option value="Outreach">Outreach</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>

                                <div className="finance-form-group">
                                    <label htmlFor="budgetAmount">Budget Amount (Optional)</label>
                                    <input
                                        type="number"
                                        id="budgetAmount"
                                        className="finance-form-control"
                                        placeholder="0"
                                        value={expenseFormData.budgetAmount}
                                        onChange={handleExpenseInputChange}
                                        min="0"
                                        step="0.01"
                                    />
                                </div>
                            </div>

                            <div className="finance-form-group">
                                <label htmlFor="expenseStartDate">Start Date</label>
                                <input
                                    type="date"
                                    id="startDate"
                                    className="finance-form-control"
                                    value={expenseFormData.startDate}
                                    onChange={handleExpenseInputChange}
                                />
                            </div>

                            <div className="finance-form-actions">
                                <button
                                    type="button"
                                    className="finance-btn-secondary"
                                    onClick={() => setShowExpenseModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="finance-btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-save"></i>
                                            {editingExpense ? 'Update Expense' : 'Add Expense'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Transaction Modal */}
            {showTransactionModal && (
                <div className="finance-modal" onClick={() => setShowTransactionModal(false)}>
                    <div className="finance-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="finance-modal-header">
                            <h3 className="finance-modal-title">
                                {editingTransaction ? 'Edit Transaction' : 'Add New Transaction'}
                            </h3>
                            <button
                                className="finance-modal-close"
                                onClick={() => setShowTransactionModal(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="finance-form">
                            <div className="finance-form-row">
                                <div className="finance-form-group">
                                    <label htmlFor="transactionDate">Date *</label>
                                    <input
                                        type="date"
                                        id="date"
                                        className="finance-form-control"
                                        value={formData.date}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className="finance-form-group">
                                    <label htmlFor="transactionType">Type *</label>
                                    <select
                                        id="type"
                                        className="finance-form-control"
                                        value={formData.type}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="income">Income</option>
                                        <option value="expense">Expense</option>
                                    </select>
                                </div>
                            </div>

                            <div className="finance-form-group">
                                <label htmlFor="transactionDescription">Description *</label>
                                <input
                                    type="text"
                                    id="description"
                                    className="finance-form-control"
                                    placeholder="Brief description of transaction"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="finance-form-row">
                                <div className="finance-form-group">
                                    <label htmlFor="transactionDepartment">Department *</label>
                                    <select
                                        id="department"
                                        className="finance-form-control"
                                        value={formData.department}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Select Department</option>
                                        {departments.map(dept => (
                                            <option key={dept.value} value={dept.value}>
                                                {dept.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="finance-form-group">
                                    <label htmlFor="transactionReason">Reason *</label>
                                    <select
                                        id="reason"
                                        className="finance-form-control"
                                        value={formData.reason}
                                        onChange={handleInputChange}
                                        required
                                        disabled={!formData.department}
                                    >
                                        <option value="">Select Reason</option>
                                        {formData.department && getReasonOptions(formData.department).map(reason => (
                                            <option key={reason} value={reason}>
                                                {reason}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {formData.type === 'income' && (
                                <div className="finance-form-row">
                                    <div className="finance-form-group">
                                        <label htmlFor="transactionSourceId">Income Source</label>
                                        <select
                                            id="sourceId"
                                            className="finance-form-control"
                                            value={formData.sourceId}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">Select Source</option>
                                            {incomeSources.map(source => (
                                                <option key={source.id} value={source.id}>
                                                    {source.name}
                                                </option>
                                            ))}
                                            <option value="new">+ Add New Source</option>
                                        </select>
                                    </div>

                                    {formData.sourceId === 'new' && (
                                        <div className="finance-form-group">
                                            <label htmlFor="transactionSourceName">New Source Name</label>
                                            <input
                                                type="text"
                                                id="sourceName"
                                                className="finance-form-control"
                                                placeholder="Enter new source name"
                                                value={formData.sourceName}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                    )}

                                    <div className="finance-form-group">
                                        <label htmlFor="transactionCategory">Category</label>
                                        <input
                                            type="text"
                                            id="category"
                                            className="finance-form-control"
                                            placeholder="e.g., Tithe, Offering"
                                            value={formData.category}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="finance-form-row">
                                <div className="finance-form-group">
                                    <label htmlFor="transactionAmount">Amount (Ksh) *</label>
                                    <input
                                        type="number"
                                        id="amount"
                                        className="finance-form-control"
                                        placeholder="0.00"
                                        value={formData.amount}
                                        onChange={handleInputChange}
                                        min="0"
                                        step="0.01"
                                        required
                                    />
                                </div>

                                <div className="finance-form-group">
                                    <label htmlFor="transactionNotes">Notes (Optional)</label>
                                    <input
                                        type="text"
                                        id="notes"
                                        className="finance-form-control"
                                        placeholder="Additional notes"
                                        value={formData.notes}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div className="finance-form-actions">
                                <button
                                    type="button"
                                    className="finance-btn-secondary"
                                    onClick={() => setShowTransactionModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="finance-btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-save"></i>
                                            {editingTransaction ? 'Update Transaction' : 'Add Transaction'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Finance;
